import { useEffect, useState } from "react";
import { Activity, Eye, MousePointer2, Users, UserX, Smartphone, TrendingUp, Flame, BellRing } from "lucide-react";
import api from "@/lib/api";

const Stat = ({ id, icon: Icon, label, value, color }) => (
  <div data-testid={`live-stat-${id}`} className="bg-card border border-white/10 rounded-xl p-5 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-lg grid place-items-center ${color}`}><Icon size={22} /></div>
    <div>
      <div className="text-3xl font-head font-extrabold tabular-nums">{value ?? "—"}</div>
      <div className="text-xs text-zinc-400 uppercase tracking-wider">{label}</div>
    </div>
  </div>
);

const TopList = ({ id, title, icon: Icon, items, empty }) => (
  <div data-testid={id} className="bg-card border border-white/10 rounded-2xl p-5">
    <h3 className="font-head font-bold mb-4 flex items-center gap-2"><Icon size={17} className="text-brand" /> {title}</h3>
    {items?.length ? (
      <ol className="space-y-2">
        {items.map((c, i) => (
          <li key={c.id} className="flex items-center gap-3 text-sm">
            <span className="w-6 text-zinc-500 font-bold tabular-nums">{i + 1}</span>
            {c.poster ? <img src={c.poster} alt="" className="w-10 h-7 object-cover rounded bg-white/5" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} /> : <span className="w-10 h-7 rounded bg-white/5" />}
            <span className="flex-1 truncate">{c.title}</span>
            <span className="font-bold tabular-nums text-zinc-200">{c.count}</span>
          </li>
        ))}
      </ol>
    ) : <p className="text-sm text-zinc-500 py-4">{empty}</p>}
  </div>
);

export const LivePanel = () => {
  const [d, setD] = useState(null);

  useEffect(() => {
    const load = () => api.get("/admin/live").then(({ data }) => setD(data)).catch(() => {});
    load();
    const iv = setInterval(load, 10000);
    return () => clearInterval(iv);
  }, []);

  return (
    <div data-testid="admin-live-panel" className="space-y-6">
      <div className="flex items-center gap-2 text-sm text-zinc-400">
        <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" /><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" /></span>
        Live · refreshes every 10s{d && <span data-testid="live-updated-at"> · updated {new Date(d.updated_at).toLocaleTimeString()}</span>}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat id="online" icon={Activity} label="Online now" value={d?.online} color="bg-green-500/20 text-green-400" />
        <Stat id="watching" icon={Eye} label="Watching now" value={d?.watching} color="bg-brand/20 text-brand" />
        <Stat id="browsing" icon={MousePointer2} label="Browsing now" value={d?.browsing} color="bg-blue-500/20 text-blue-400" />
        <Stat id="members-online" icon={Users} label="Members online" value={d?.members_online} color="bg-gold/20 text-gold" />
        <Stat id="guests-online" icon={UserX} label="Guests online" value={d?.guests_online} color="bg-white/10 text-white" />
        <Stat id="members-total" icon={Users} label="Total members" value={d?.members_total} color="bg-white/10 text-white" />
        <Stat id="apps-active" icon={Smartphone} label="Active apps (30d)" value={d?.apps_active} color="bg-pink/20 text-pink" />
        <Stat id="apps-total" icon={Smartphone} label="Apps installed (all time)" value={d?.apps_total} color="bg-pink/10 text-pink" />
        <Stat id="push-devices" icon={BellRing} label="Phone alerts on" value={d?.push_devices} color="bg-emerald-500/20 text-emerald-300" />
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <TopList id="live-top-now" title="Most watched right now" icon={Flame} items={d?.top_now} empty="Nobody is watching right now." />
        <TopList id="live-top-week" title="Most watched — last 7 days" icon={TrendingUp} items={d?.top_week} empty="No plays recorded yet." />
      </div>
    </div>
  );
};
